import { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { fetchIfTimeRangeHasData } from 'api/queries/iterableGoals';
import {
  defaultDateParams,
  defaultTimePreset,
  defaultTimezone,
  RELATIVE_TIME_PRESETS,
} from 'modules/iterableGoals/constants';
import { useQueryParamContext, useTimeFilterContext } from 'modules/iterableGoals/contexts';
import { useGoalsTranslations } from 'modules/iterableGoals/hooks';
import { DateParams, TimeParams, TimePreset } from 'modules/iterableGoals/types';
import { FACET_ACTIONS, useRumAction } from 'services/observability/datadog';
import { DATE_FORMAT_TRUNC_YEAR, displayDate } from 'utils/date';
import { DateRangePicker } from 'ui/datePicker';
import { colors } from 'ui/theme';
import { useClickAway } from 'react-use';
import {
  DatePickerContainer,
  DatePickerTriggerButton,
  DatePickerWrapper,
  StyledChevronDown,
  TimeRangeHeaderWrapper,
} from './Styled';

export const AnalyticsTimeRangeHeader = () => {
  const addSignal = useRumAction(FACET_ACTIONS);
  const [dateParams, setDateParams] = useState<DateParams>(defaultDateParams);
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [timePreset, setTimePreset] = useState(defaultTimePreset);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);
  const isMountRef = useRef(false);
  const { timeParams, updateTimeParams } = useTimeFilterContext();
  const { goalName } = useQueryParamContext();
  const { t, ct } = useGoalsTranslations();

  const startTime = dateParams.startDate?.toISOString();
  const endTime = dateParams.endDate?.toISOString();

  const [checkIfTimeRangeHasData] = useLazyQuery(fetchIfTimeRangeHasData, {
    variables: {
      goalName,
      count: 1,
      startTime,
      endTime,
    },
    onCompleted: (data) => {
      if (isMountRef.current) {
        addSignal('TIME_FILTER_UPDATED');
      }
      const { contributions, sends } = data.bestWorstCampaignStatsForGoal.best?.[0] ?? {};
      const timeRangeHasCampaigns = data.bestWorstCampaignStatsForGoal.best.length > 0;
      const timeRangeHasContribution = (contributions !== 0 || sends !== 0) && contributions > 0;
      const timeRangeHasData = timeRangeHasCampaigns && timeRangeHasContribution;

      const updatedTimeParams: TimeParams = {
        ...timeParams,
        // Updating the start and end time context only
        // if the time range has data.
        ...(timeRangeHasData && {
          startTime,
          endTime,
        }),
        timeRangeHasData,
      };
      updateTimeParams(updatedTimeParams);
      isMountRef.current = true;
    },
  });

  useClickAway(datePickerRef, () => setShowDatePicker(false));

  /**
   * The dateRange string will let us know when we would have
   * to update the 'TimeFilterContext'. Since, the DateRangePicker
   * component will internally be updating multiple time setter states.
   * For example, a 'timePreset' change will trigger a 'dateParams' update.
   * So, it's hard to just key of from the individual setter state to update the context.
   */
  const dateRange = [startTime, endTime, timezone, timePreset].join('').replace(/GMT-\d{4}/g, '');

  useEffect(() => {
    checkIfTimeRangeHasData();
  }, [dateRange]);

  return (
    <TimeRangeHeaderWrapper data-test="goal-analytics-time-range-header">
      <span>{t('labels.timeRangeHeader')}</span>
      <DatePickerContainer>
        <DatePickerTriggerButton
          onClick={() => setShowDatePicker((state) => !state)}
          data-test="goal-analytics-time-range-trigger-button"
        >
          {ct(
            RELATIVE_TIME_PRESETS[timePreset].filterLabel,
            timePreset === TimePreset.CUSTOM_RANGE
              ? {
                  startDate: displayDate(dateParams?.startDate, DATE_FORMAT_TRUNC_YEAR),
                  endDate: displayDate(dateParams?.endDate, DATE_FORMAT_TRUNC_YEAR),
                }
              : {},
          )}
          <StyledChevronDown height={14} color={colors.grey50} />
        </DatePickerTriggerButton>
        {showDatePicker && (
          <DatePickerWrapper ref={datePickerRef} data-test="goal-analytics-time-range-picker">
            <DateRangePicker
              startDate={dateParams.startDate}
              endDate={dateParams.endDate}
              onDateRangeChange={({ startDate, endDate }) => {
                setDateParams({ startDate, endDate });
              }}
              defaultTimeZone={timezone}
              onTimeZoneChange={(tz) => setTimezone(tz)}
              dateRangePreset={timePreset}
              onPresetChange={(preset) => setTimePreset(preset as TimePreset)}
              presets={RELATIVE_TIME_PRESETS}
              promoteCustomInput
              blockFutureDates
              showRelativeDatePresets
            />
          </DatePickerWrapper>
        )}
      </DatePickerContainer>
    </TimeRangeHeaderWrapper>
  );
};
